// src/pages/PublicDashboard.jsx
// Public view-only performance page
// No login required — anyone with the link can view
// Shows stats, equity curve, pairs, sessions — NO $ amounts
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import ODFLogo from '../components/ODFLogo';
import { detectAnomalies, getCredibilityTier } from '../utils/odfScore';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, BarChart, Bar, Cell
} from 'recharts';
import { Link2, Check, Search } from 'lucide-react';

const G = '#00e5a0';

function calcStats(trades, startBal = 10000) {
  if (!trades.length) return { total:0, wins:0, losses:0, be:0, winRate:0, avgRR:'0.0', profitFactor:'0.00', discipline:100, roi:0 };
  const wins   = trades.filter(t => t.result === 'Win').length;
  const losses = trades.filter(t => t.result === 'Loss').length;
  const be     = trades.filter(t => t.result === 'Breakeven').length;
  const total  = trades.length;
  const winRate= Math.round((wins / total) * 100);

  const rrs    = trades.filter(t => t.rr_achieved && t.rr_achieved > 0).map(t => Number(t.rr_achieved));
  const avgRR  = rrs.length ? (rrs.reduce((a,b) => a+b, 0) / rrs.length).toFixed(1) : '0.0';

  const grossW = trades.filter(t => Number(t.pnl) > 0).reduce((a,t) => a + Number(t.pnl), 0);
  const grossL = Math.abs(trades.filter(t => Number(t.pnl) < 0).reduce((a,t) => a + Number(t.pnl), 0));
  const pf     = grossL > 0 ? (grossW / grossL).toFixed(2) : grossW > 0 ? '∞' : '0.00';

  const followed = trades.filter(t => t.followed_rules === true || t.followed_rules === 'true').length;
  const disc     = Math.round((followed / total) * 100);

  // ROI uses the REAL starting balance (passed in from the active account),
  // not a hardcoded $10K. Falls back to 10K only if no balance is provided.
  const totalPnl = trades.reduce((a,t) => a + Number(t.pnl||0), 0);
  const base = startBal > 0 ? startBal : 10000;
  const roi = Number(((totalPnl / base) * 100).toFixed(2));
  return { total, wins, losses, be, winRate, avgRR, profitFactor: pf, discipline: disc, roi };
}

function StatCard({ label, value, color = '#f0f0f5', sub }) {
  return (
    <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:12, padding:'18px 20px' }}>
      <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:700, color, fontFamily:"'DM Mono',monospace", letterSpacing:'-0.02em' }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'#55556a', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) return (
    <div style={{ background:'#16161f', border:'1px solid #2a2a3e', borderRadius:8, padding:'10px 14px', fontSize:12 }}>
      <div style={{ color:'#55556a', fontFamily:"'DM Mono',monospace", fontSize:10, marginBottom:4 }}>{label}</div>
      <div style={{ color:G, fontFamily:"'DM Mono',monospace" }}>
        {payload[0].value > 0 ? '+' : ''}{Number(payload[0].value).toFixed(1)}%
      </div>
    </div>
  );
  return null;
};

export default function PublicDashboard({ userId, onBack, onSignIn }) {
  const [trades,   setTrades]   = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [profile,  setProfile]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [activeTab,setActiveTab]= useState('all');
  const [copied,   setCopied]   = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!userId) { setNotFound(true); setLoading(false); return; }
    async function load() {
      try {
        const [{ data: profileData }, { data: tradesData }, { data: accountsData }] = await Promise.all([
          supabase.from('profiles').select('*').eq('id', userId).single(),
          supabase.from('trades').select('*').eq('user_id', userId).order('date'),
          supabase.from('accounts').select('id, name, type, starting_balance').eq('user_id', userId),
        ]);

        // If profile not found OR profile exists but is private — still show
        // We allow sharing regardless of is_public toggle
        // is_public only affects whether profile shows in discovery
        if (!profileData) { setNotFound(true); setLoading(false); return; }

        setProfile(profileData);
        setTrades(tradesData || []);
        setAccounts(accountsData || []);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [userId]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const filteredTrades = activeTab === 'all'
    ? trades
    : trades.filter(t => t.account_id === activeTab);

  // Real starting balance for the current view:
  //   - "All accounts" tab → sum of all account starting balances
  //   - Specific account tab → that account's starting balance
  // Falls back to $10K only if no balance data is available.
  const startBal = (() => {
    if (activeTab === 'all') {
      const total = accounts.reduce((sum, a) => sum + Number(a.starting_balance || 0), 0);
      return total > 0 ? total : 10000;
    }
    const acc = accounts.find(a => a.id === activeTab);
    const bal = Number(acc?.starting_balance);
    return bal > 0 ? bal : 10000;
  })();

  const s = calcStats(filteredTrades, startBal);
  const anomalies    = detectAnomalies(filteredTrades);
  const credibility  = getCredibilityTier(filteredTrades.length, anomalies, {});

  // Equity curve — % based, no $ values shown publicly.
  // Each trade's % return is computed against the REAL starting balance,
  // so the curve reflects the actual ROI growth — not a normalised fiction.
  const equityData = (() => {
    const sorted = [...filteredTrades].sort((a,b) => new Date(a.date) - new Date(b.date));
    let cumulative = 0;
    const base = startBal;
    return sorted.map(t => {
      const pct = base > 0 ? ((Number(t.pnl) / base) * 100) : 0;
      cumulative += pct;
      return { date: t.date?.slice(5), pct: Number(cumulative.toFixed(2)) };
    });
  })();

  // Monthly % returns — also against real starting balance
  const monthlyData = (() => {
    const byMonth = {};
    for (const t of filteredTrades) {
      const m = t.date?.slice(0, 7);
      if (!m) continue;
      if (!byMonth[m]) byMonth[m] = 0;
      byMonth[m] += Number(t.pnl) || 0;
    }
    const base = startBal;
    return Object.entries(byMonth)
      .sort((a,b) => a[0].localeCompare(b[0]))
      .map(([month, pnl]) => ({
        month: month.slice(5), // MM only
        pct: Number((base > 0 ? (pnl / base) * 100 : 0).toFixed(1))
      }))
      .slice(-12);
  })();

  // Pair performance
  const pairData = (() => {
    const p = {};
    for (const t of filteredTrades) {
      if (!t.pair) continue;
      if (!p[t.pair]) p[t.pair] = { wins:0, total:0 };
      p[t.pair].total++;
      if (t.result === 'Win') p[t.pair].wins++;
    }
    return Object.entries(p)
      .map(([pair, d]) => ({ pair, wr: Math.round(d.wins/d.total*100), total: d.total }))
      .sort((a,b) => b.total - a.total)
      .slice(0, 8);
  })();

  // Session performance
  const sessionData = (() => {
    const sess = {};
    for (const t of filteredTrades) {
      const s = t.session || 'Unknown';
      if (!sess[s]) sess[s] = { wins:0, total:0 };
      sess[s].total++;
      if (t.result === 'Win') sess[s].wins++;
    }
    return Object.entries(sess)
      .map(([session, d]) => ({ session, wr: Math.round(d.wins/d.total*100), total: d.total }))
      .sort((a,b) => b.total - a.total);
  })();

  // Win streak
  const streaks = (() => {
    const sorted = [...filteredTrades].sort((a,b) => new Date(a.date)-new Date(b.date));
    let cur = 0, best = 0;
    for (const t of sorted) {
      if (t.result === 'Win') { cur++; best = Math.max(best, cur); }
      else cur = 0;
    }
    return { current: cur, best };
  })();

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#0a0a0f', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16 }}>
      <div style={{ width:40, height:40, borderRadius:'50%', border:'2px solid #1e1e2e', borderTopColor:G, animation:'spin 0.8s linear infinite' }} />
      <div style={{ color:'#55556a', fontFamily:"'DM Mono',monospace", fontSize:11 }}>Loading performance data...</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (notFound) return (
    <div style={{ minHeight:'100vh', background:'#0a0a0f', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16, fontFamily:"'IBM Plex Sans',sans-serif" }}>
      <div style={{ width:56, height:56, borderRadius:14, background:'rgba(255,255,255,0.03)', border:'1px solid #1e1e2e', display:'flex', alignItems:'center', justifyContent:'center', color:'#55556a' }}><Search size={24} strokeWidth={1.6} /></div>
      <div style={{ fontSize:18, fontWeight:600, color:'#f0f0f5' }}>Profile not found</div>
      <div style={{ fontSize:14, color:'#8888a8' }}>This link may be invalid or expired.</div>
      <div style={{ display:'flex', gap:10, marginTop:8 }}>
        <button onClick={onBack} style={{ background:'transparent', color:'#8888a8', border:'1px solid #1e1e2e', borderRadius:8, padding:'10px 20px', fontSize:13, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>← Home</button>
        <button onClick={onSignIn} style={{ background:G, color:'#0a0a0f', border:'none', borderRadius:8, padding:'10px 20px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>Sign In →</button>
      </div>
    </div>
  );

  const displayName = profile?.username ? `@${profile.username}` : profile?.full_name || 'Trader';

  return (
    <div style={{ background:'#0a0a0f', minHeight:'100vh', color:'#f0f0f5', fontFamily:"'IBM Plex Sans',sans-serif" }}>
      {/* Nav */}
      <nav style={{ position:'sticky', top:0, zIndex:50, background:'rgba(10,10,15,0.95)', backdropFilter:'blur(12px)', borderBottom:'1px solid #1e1e2e', padding:'0 32px', height:58, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ cursor:'pointer' }} onClick={onBack}><ODFLogo size="compact" /></div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", display:'none' }} className="desktop-only">
            View-only · {filteredTrades.length} trades
          </div>
          <button onClick={copyLink} style={{ display:'flex', alignItems:'center', gap:6, background:'transparent', border:`1px solid ${copied?'rgba(0,229,160,0.3)':'#1e1e2e'}`, borderRadius:8, padding:'7px 14px', cursor:'pointer', color:copied?G:'#8888a8', fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s' }}>
            {copied ? <><Check size={13} /> Copied</> : <><Link2 size={13} /> Share</>}
          </button>
          <button onClick={onSignIn} style={{ background:G, color:'#0a0a0f', border:'none', borderRadius:8, padding:'8px 20px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>
            Sign In →
          </button>
        </div>
      </nav>

      <div style={{ maxWidth:1060, margin:'0 auto', padding:'40px 24px' }}>

        {/* Profile header */}
        <div style={{ textAlign:'center', marginBottom:36 }}>
          <div style={{ width:64, height:64, borderRadius:'50%', background:'rgba(0,229,160,0.1)', border:'1px solid rgba(0,229,160,0.2)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px', fontSize:22, color:G, fontFamily:"'Georgia',serif" }}>
            {displayName[0]?.replace('@','')?.toUpperCase() || 'T'}
          </div>
          <div style={{ fontSize:22, fontWeight:700, marginBottom:4, letterSpacing:'-0.01em' }}>{displayName}</div>
          <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(0,229,160,0.08)', border:'1px solid rgba(0,229,160,0.2)', borderRadius:100, padding:'4px 14px', fontSize:11, color:G, fontFamily:"'DM Mono',monospace" }}>
            <span style={{ width:5, height:5, borderRadius:'50%', background:G, display:'inline-block' }} />
            LIVE · {filteredTrades.length} TRADES TRACKED
          </div>
        </div>

        {/* Account tabs */}
        {accounts.length > 1 && (
          <div style={{ display:'flex', gap:6, marginBottom:28, flexWrap:'wrap', justifyContent:'center' }}>
            <button onClick={() => setActiveTab('all')} style={{ padding:'7px 18px', borderRadius:8, border:'1px solid', fontSize:12, cursor:'pointer', outline:'none', fontFamily:"'IBM Plex Sans',sans-serif", background:activeTab==='all'?'rgba(0,229,160,0.1)':'transparent', borderColor:activeTab==='all'?'rgba(0,229,160,0.3)':'#1e1e2e', color:activeTab==='all'?G:'#8888a8' }}>
              All Accounts
            </button>
            {accounts.map(acc => (
              <button key={acc.id} onClick={() => setActiveTab(acc.id)} style={{ padding:'7px 18px', borderRadius:8, border:'1px solid', fontSize:12, cursor:'pointer', outline:'none', fontFamily:"'IBM Plex Sans',sans-serif", background:activeTab===acc.id?'rgba(0,229,160,0.1)':'transparent', borderColor:activeTab===acc.id?'rgba(0,229,160,0.3)':'#1e1e2e', color:activeTab===acc.id?G:'#8888a8' }}>
                {acc.name}
              </button>
            ))}
          </div>
        )}

        {filteredTrades.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 0', color:'#55556a', fontSize:14 }}>
            No trades logged yet for this account.
          </div>
        ) : (
          <>
            {/* Key stats */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))', gap:12, marginBottom:20 }}>
              <StatCard label="Win Rate"       value={`${s.winRate}%`}       color={G}  sub={`${s.wins}W · ${s.losses}L · ${s.be}BE`} />
              <StatCard label="ROI"            value={`${s.roi >= 0 ? '+' : ''}${s.roi}%`} color={s.roi >= 0 ? G : '#ff4d6d'} sub="Return on capital" />
              <StatCard label="Avg RR"         value={`${s.avgRR}R`}         color={G}                                                          sub="Per trade" />
              <StatCard label="Profit Factor"  value={s.profitFactor}        color={parseFloat(s.profitFactor)>=1.5?G:'#ffd166'}               sub="Gross P / L" />
              <StatCard label="Discipline"     value={`${s.discipline}%`}    color={s.discipline>=80?G:'#ffd166'}                               sub="Rule adherence" />
              <StatCard label="Total Trades"   value={s.total}               sub="All time" />
              <StatCard label="Best Streak"    value={`${streaks.best}W`}    color={G}                                                          sub={`Current: ${streaks.current}`} />
            </div>

            {/* Equity curve */}
            {equityData.length > 1 && (
              <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:'22px 24px', marginBottom:16 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:4 }}>Performance Curve</div>
                <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", marginBottom:16 }}>Cumulative % return · Balance not shown</div>
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={equityData} margin={{ top:4, right:4, left:-10, bottom:0 }}>
                    <defs>
                      <linearGradient id="pubGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={G} stopOpacity={0.2} />
                        <stop offset="95%" stopColor={G} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e1e2e" />
                    <XAxis dataKey="date" tick={{ fill:'#55556a', fontSize:9, fontFamily:"'DM Mono',monospace" }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fill:'#55556a', fontSize:9 }} tickLine={false} axisLine={false} tickFormatter={v=>`${v>0?'+':''}${v.toFixed(0)}%`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="pct" stroke={G} strokeWidth={2} fill="url(#pubGrad)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
              {/* Monthly returns */}
              {monthlyData.length > 0 && (
                <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:'22px 24px' }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:16 }}>Monthly Returns %</div>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={monthlyData} margin={{ top:4, right:4, left:-10, bottom:0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e1e2e" />
                      <XAxis dataKey="month" tick={{ fill:'#55556a', fontSize:9 }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fill:'#55556a', fontSize:9 }} tickLine={false} axisLine={false} tickFormatter={v=>`${v>0?'+':''}${v}%`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="pct" radius={[4,4,0,0]}>
                        {monthlyData.map((entry, i) => (
                          <Cell key={i} fill={entry.pct >= 0 ? G : '#ff4d6d'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Pair performance */}
              {pairData.length > 0 && (
                <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:'22px 24px' }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:16 }}>Performance by Pair</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {pairData.map((p, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <span style={{ fontSize:11, color:'#f0f0f5', fontWeight:500, minWidth:70 }}>{p.pair}</span>
                        <div style={{ flex:1, background:'#1e1e2e', borderRadius:4, height:6, overflow:'hidden' }}>
                          <div style={{ width:`${p.wr}%`, height:'100%', background:p.wr>=60?G:p.wr>=40?'#ffd166':'#ff4d6d', borderRadius:4, transition:'width 0.5s ease' }} />
                        </div>
                        <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", fontWeight:600, minWidth:36, textAlign:'right', color:p.wr>=60?G:p.wr>=40?'#ffd166':'#ff4d6d' }}>{p.wr}%</span>
                        <span style={{ fontSize:10, color:'#55556a', minWidth:24 }}>{p.total}t</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Session performance */}
            {sessionData.length > 0 && (
              <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:'22px 24px', marginBottom:16 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:16 }}>Performance by Session</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:10 }}>
                  {sessionData.map((s, i) => (
                    <div key={i} style={{ background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:10, padding:'12px 14px' }}>
                      <div style={{ fontSize:11, color:'#55556a', marginBottom:6 }}>{s.session}</div>
                      <div style={{ fontSize:18, fontWeight:700, color:s.wr>=60?G:s.wr>=40?'#ffd166':'#ff4d6d', fontFamily:"'DM Mono',monospace" }}>{s.wr}%</div>
                      <div style={{ fontSize:10, color:'#55556a', marginTop:2 }}>{s.total} trades</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* CTA for non-users */}
        <div style={{ background:'rgba(0,229,160,0.05)', border:'1px solid rgba(0,229,160,0.15)', borderRadius:14, padding:'28px 32px', textAlign:'center', marginTop:24 }}>
          <div style={{ fontSize:16, fontWeight:600, color:'#f0f0f5', marginBottom:8 }}>Track your own performance</div>
          <div style={{ fontSize:13, color:'#8888a8', marginBottom:20, lineHeight:1.7 }}>
            ODFTrades is a free trading performance system. Log trades, analyse your data, and build a verifiable trade data analytics.
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
            <button onClick={onSignIn} style={{ background:G, color:'#0a0a0f', border:'none', borderRadius:9, padding:'11px 28px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>
              Start Free →
            </button>
            <button onClick={onBack} style={{ background:'transparent', color:'#8888a8', border:'1px solid #1e1e2e', borderRadius:9, padding:'11px 28px', fontSize:13, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>
              Learn More
            </button>
          </div>
        </div>

        <div style={{ textAlign:'center', fontSize:11, color:'#2a2a3e', fontFamily:"'DM Mono',monospace", marginTop:28 }}>
          ODFTrades · Past performance does not guarantee future results · Trading involves risk
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
